"use client";

import { useEffect, useRef, useState } from "react";

/**
 * Drives a carousel's "advance every N ms" timer, and stops it whenever
 * advancing would be pointless or unwelcome.
 *
 * WHY THIS EXISTS
 *
 * The marketing sections each grew their own `setInterval` auto-rotator, and
 * none of them had a reason to stop. A `setInterval` that calls `setState`
 * does not care that its section is four screens above the fold: it keeps
 * waking the main thread, re-rendering a subtree, and — because these sections
 * animate the change — handing framer-motion a fresh transition to run against
 * elements nobody can see. On the home page that was three independent timers
 * firing for the whole visit. On a phone, where that same thread has to
 * service scrolling, it is a steady tax for no benefit at all.
 *
 * So the timer here only runs when all three of these hold:
 *
 *   - the section is in (or near) the viewport — an IntersectionObserver, not
 *     a one-shot "has been seen" flag, so it stops again on the way past;
 *   - the tab is foregrounded — `visibilitychange`, because a background tab
 *     is the one case where the browser will not throttle a timer enough to
 *     make it free, and there is definitionally no one watching;
 *   - the visitor has not asked for reduced motion — in which case a carousel
 *     that advances on its own is exactly what they opted out of, so it holds
 *     on the first item and stays there.
 *
 * `pause()`/`resume()` are returned for the hover- and focus-to-pause
 * behaviour the sections already had.
 *
 * @param count    number of items to cycle through
 * @param intervalMs delay between advances
 */
export default function useAutoRotate(count: number, intervalMs: number) {
  const containerRef = useRef<HTMLElement | null>(null);
  const [index, setIndex] = useState(0);
  const [hovered, setHovered] = useState(false);
  const [visible, setVisible] = useState(false);

  // Visibility of the section itself.
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return undefined;

    if (typeof IntersectionObserver === "undefined") {
      setVisible(true);
      return undefined;
    }

    const obs = new IntersectionObserver(
      ([entry]) => setVisible(entry.isIntersecting),
      // A little margin so the first advance has already happened by the time
      // the section is properly on screen, rather than the visitor arriving to
      // a still frame that only starts moving after a full interval.
      { rootMargin: "200px" },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  // Foreground/background, and the reduced-motion preference. Both are live
  // subscriptions: the visitor can switch tabs, and on every current platform
  // the OS motion setting can change without a reload.
  const [awake, setAwake] = useState(true);
  const [stillness, setStillness] = useState(false);

  useEffect(() => {
    const onVisibility = () => setAwake(document.visibilityState === "visible");
    onVisibility();
    document.addEventListener("visibilitychange", onVisibility);

    const mq = window.matchMedia?.("(prefers-reduced-motion: reduce)");
    const onMotion = () => setStillness(Boolean(mq?.matches));
    onMotion();
    mq?.addEventListener("change", onMotion);

    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      mq?.removeEventListener("change", onMotion);
    };
  }, []);

  useEffect(() => {
    if (!visible || !awake || stillness || hovered || count < 2) return undefined;

    const timer = setInterval(() => {
      setIndex((prev) => (prev + 1) % count);
    }, intervalMs);
    return () => clearInterval(timer);
  }, [visible, awake, stillness, hovered, count, intervalMs]);

  // A shrinking list (a dictionary swap on a language change) must not leave
  // the index pointing past the end.
  useEffect(() => {
    setIndex((prev) => (prev < count ? prev : 0));
  }, [count]);

  return {
    containerRef,
    index,
    setIndex,
    pause: () => setHovered(true),
    resume: () => setHovered(false),
    /** True when the timer is not currently advancing, for any reason.
        Sections use it to hide a progress indicator that would otherwise
        animate against a clock that isn't running. */
    paused: hovered || !visible || !awake || stillness,
  };
}
